import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { hospitalAPI, authAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';

const HospitalDashboard = () => {
  const { showSuccess, showError } = useNotification();
  const [stats, setStats] = useState({
    totalPatients: 0,
    occupiedBeds: 0,
    availableBeds: 0,
    revenue: 0
  });
  // const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showBedModal, setShowBedModal] = useState(false);

  const [activeTab, setActiveTab] = useState('stats');
  const [doctors, setDoctors] = useState([]);
  const [showAddDoctor, setShowAddDoctor] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    specialization: '',
    license_number: '',
    profile_image: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await hospitalAPI.getStats();
      if (res.data?.success) {
        setStats(res.data.data || stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const res = await hospitalAPI.getDoctors();
      if (res.data?.success) {
        setDoctors(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  // const [showValues, setShowValues] = useState(false);
  const [createdDoctorKey, setCreatedDoctorKey] = useState(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('profile_image', file);

    try {
      setUploading(true);
      const res = await authAPI.uploadTempImage(formData);
      if (res.data.success) {
        setNewDoctor({ ...newDoctor, profile_image: res.data.data.filename });
        showSuccess('Image uploaded successfully');
      }
    } catch (error) {
      showError('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    if (!newDoctor.profile_image) {
      return showError('Please upload a profile image first');
    }
    try {
      const res = await hospitalAPI.addDoctor(newDoctor);
      if (res.data?.success) {
        showSuccess('Doctor added successfully!');
        setShowAddDoctor(false);
        setNewDoctor({ name: '', email: '', phone: '', specialization: '', license_number: '', profile_image: '' });

        // Show Secret Key Modal
        if (res.data.data.secretKey) {
          setCreatedDoctorKey({
            name: res.data.data.doctor.name,
            key: res.data.data.secretKey
          });
        }

        fetchDoctors();
      } else {
        showError(res.data?.message || 'Failed to add doctor');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Error adding doctor');
    }
  };

  // ... (render logic)

  return (
    <div className="space-y-6">
      {/* Secret Key Modal */}
      {createdDoctorKey && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 p-8 rounded-xl border-2 border-yellow-500 max-w-md w-full text-center shadow-2xl">
            <div className="text-yellow-500 text-5xl mb-4">🔐</div>
            <h3 className="text-2xl font-bold text-white mb-2">Doctor Account Created</h3>
            <p className="text-gray-300 mb-6">
              Please share this Secret Key with <strong>{createdDoctorKey.name}</strong> immediately.
              <br /><span className="text-yellow-400 text-sm">This key will not be shown again.</span>
            </p>

            <div className="bg-gray-900 border border-yellow-500/30 rounded-lg p-4 mb-6 relative group">
              <p className="text-4xl font-mono text-yellow-500 tracking-widest font-bold select-all">
                {createdDoctorKey.key}
              </p>
              <p className="text-xs text-gray-500 mt-2">Click to copy</p>
            </div>

            <button
              onClick={() => {
                navigator.clipboard.writeText(createdDoctorKey.key);
                showSuccess('Copied to clipboard');
              }}
              className="w-full bg-gray-700 text-white py-3 rounded-lg font-medium mb-3 hover:bg-gray-600 mb-3"
            >
              Copy Key
            </button>

            <button
              onClick={() => setCreatedDoctorKey(null)}
              className="w-full bg-yellow-500 text-dark py-3 rounded-lg font-bold hover:opacity-90"
            >
              I have saved this key
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-yellow-500">Hospital Dashboard</h1>
        <div className="flex space-x-2">
          {/* ... tabs ... */}
          <button
            onClick={() => setActiveTab('stats')}
            className={`px-4 py-2 rounded-lg ${activeTab === 'stats' ? 'bg-yellow-500 text-dark' : 'bg-gray-800 text-light'}`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('doctors')}
            className={`px-4 py-2 rounded-lg ${activeTab === 'doctors' ? 'bg-yellow-500 text-dark' : 'bg-gray-800 text-light'}`}
          >
            Doctors ({doctors.length}/2)
          </button>
        </div>
      </div>

      {activeTab === 'stats' ? (
        <>
          {/* ... stats ... */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
              <h3 className="text-gray-400 text-sm mb-2">Total Patients</h3>
              <p className="text-2xl font-bold text-yellow-500">{stats.totalPatients}</p>
            </div>
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
              <h3 className="text-gray-400 text-sm mb-2">Occupied Beds</h3>
              <p className="text-2xl font-bold text-yellow-500">{stats.occupiedBeds}</p>
            </div>
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
              <h3 className="text-gray-400 text-sm mb-2">Available Beds</h3>
              <p className="text-2xl font-bold text-yellow-500">{stats.availableBeds}</p>
            </div>
            <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
              <h3 className="text-gray-400 text-sm mb-2">Doctors</h3>
              <p className="text-2xl font-bold text-yellow-500">{doctors.length}</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
            <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button onClick={() => setActiveTab('doctors')} className="bg-yellow-500 text-dark py-3 px-4 rounded-lg font-medium hover:opacity-90 text-center">
                Manage Doctors
              </button>
              <button onClick={() => setShowAnalyticsModal(true)} className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600">
                View Reports
              </button>
              <button onClick={() => setShowBedModal(true)} className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600">
                Bed Allocation
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Hospital Doctors</h2>
            {doctors.length < 2 ? (
              <button
                onClick={() => setShowAddDoctor(true)}
                className="bg-yellow-500 text-dark px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Add Doctor
              </button>
            ) : (
              <div className="bg-gray-700 px-4 py-2 rounded-lg border border-yellow-400/50">
                <span className="text-yellow-400 font-medium text-sm">Limit Reached (2/2)</span>
              </div>
            )}
          </div>

          {showAddDoctor && (
            <div className="bg-gray-800 p-6 rounded-xl border border-yellow-500/30 relative">
              <button
                onClick={() => setShowAddDoctor(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-white"
              >
                ✕
              </button>
              <h3 className="text-yellow-500 font-bold mb-4">Add New Sub-Doctor</h3>
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4 text-sm text-blue-200">
                ℹ️ A unique 6-character Secret Key will be generated for login. No password is required.
              </div>

              <form onSubmit={handleAddDoctor} className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Doctor Name</label>
                  <input
                    placeholder="Dr. Name"
                    className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                    value={newDoctor.name}
                    onChange={e => setNewDoctor({ ...newDoctor, name: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Email Address</label>
                  <input
                    type="email"
                    placeholder="email@example.com"
                    className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                    value={newDoctor.email}
                    onChange={e => setNewDoctor({ ...newDoctor, email: e.target.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Phone Number</label>
                    <input
                      placeholder="Phone"
                      className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                      value={newDoctor.phone}
                      onChange={e => setNewDoctor({ ...newDoctor, phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">License Number</label>
                    <input
                      placeholder="License Number"
                      className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                      value={newDoctor.license_number}
                      onChange={e => setNewDoctor({ ...newDoctor, license_number: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-1">Specialization</label>
                  <input
                    placeholder="e.g. Cardiologist"
                    className="w-full bg-gray-700 border border-gray-600 p-3 rounded-lg text-light"
                    value={newDoctor.specialization}
                    onChange={e => setNewDoctor({ ...newDoctor, specialization: e.target.value })}
                    required
                  />
                </div>

                <div className="bg-gray-900/50 p-4 rounded-xl border border-dashed border-gray-700">
                  <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Profile Image (Mandatory)</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border border-gray-600">
                      {newDoctor.profile_image ? (
                        <img src={formatImageUrl(newDoctor.profile_image)} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="doctor-image-upload"
                      />
                      <label
                        htmlFor="doctor-image-upload"
                        className={`inline-block px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-colors ${uploading ? 'bg-gray-600' : 'bg-yellow-500 text-dark hover:bg-yellow-500'}`}
                      >
                        {uploading ? 'Uploading...' : 'Choose Image'}
                      </label>
                      <p className="text-[10px] text-gray-500 mt-1 italic">JPG/PNG, Max 2MB. Recommended 1:1 ratio.</p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={uploading}
                  className={`bg-yellow-500 text-dark py-3 rounded-lg font-bold mt-2 hover:opacity-90 ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Generate Secret Key & Create Account
                </button>
              </form>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            {doctors.length === 0 ? (
              <div className="bg-gray-800 p-10 text-center rounded-xl text-gray-400">
                You haven't added any doctors yet. Limit: 2.
              </div>
            ) : (
              doctors.map(doc => (
                <div key={doc._id} className="bg-gray-800 p-4 rounded-xl flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-light">{doc.name}</h3>
                    <p className="text-gray-400 text-sm">{doc.specialization}</p>
                    <p className="text-xs text-gray-500">{doc.email}</p>
                  </div>
                  <div className={`text-sm font-medium ${doc.is_approved ? 'text-green-500' : 'text-yellow-500'}`}>
                    {doc.is_approved ? 'Active' : 'Pending Approval'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-yellow-500">Hospital Analytics Summary</h3>
              <button onClick={() => setShowAnalyticsModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 text-center">
                <p className="text-gray-400 text-sm mb-1">Total Admissions</p>
                <p className="text-3xl font-bold text-white">{stats.totalPatients || 0}</p>
                <p className="text-xs text-green-400 mt-2">↑ 12% vs last month</p>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 text-center">
                <p className="text-gray-400 text-sm mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-white">₹{stats.revenue || 0}</p>
                <p className="text-xs text-green-400 mt-2">↑ 8% vs last month</p>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 text-center">
                <p className="text-gray-400 text-sm mb-1">Active Doctors</p>
                <p className="text-3xl font-bold text-white">{doctors.length || 0}</p>
              </div>
              <div className="bg-gray-700 p-4 rounded-lg border border-gray-600 text-center">
                <p className="text-gray-400 text-sm mb-1">Bed Occupancy Rate</p>
                <p className="text-3xl font-bold text-white">
                  {stats.occupiedBeds + stats.availableBeds > 0
                    ? Math.round((stats.occupiedBeds / (stats.occupiedBeds + stats.availableBeds)) * 100)
                    : 0}%
                </p>
              </div>
            </div>

            <div className="mt-6 text-right">
              <button onClick={() => setShowAnalyticsModal(false)} className="bg-gray-700 hover:bg-gray-600 text-light py-2 px-6 rounded-lg font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bed Allocation Modal */}
      {showBedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-3xl w-full p-6 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-yellow-500">Facility Bed Allocation</h3>
              <button onClick={() => setShowBedModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="flex space-x-6 mb-6 justify-center">
              <div className="flex items-center"><div className="w-4 h-4 bg-green-500 rounded mr-2"></div><span className="text-sm text-gray-300">Available ({stats.availableBeds || 0})</span></div>
              <div className="flex items-center"><div className="w-4 h-4 bg-yellow-400 rounded mr-2"></div><span className="text-sm text-gray-300">Occupied ({stats.occupiedBeds || 0})</span></div>
            </div>

            <div className="overflow-y-auto flex-1 p-4 bg-gray-900 rounded-xl">
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                {Array.from({ length: Math.min((stats.occupiedBeds || 0) + (stats.availableBeds || 0) || 50, 200) }).map((_, i) => {
                  const isOccupied = i < (stats.occupiedBeds || 0);
                  return (
                    <div
                      key={i}
                      className={`aspect-square rounded flex flex-col items-center justify-center p-1 border border-black/20 ${isOccupied ? 'bg-yellow-400/80 text-white' : 'bg-green-500/80 text-white'}`}
                      title={`Bed ${i + 1}: ${isOccupied ? 'Occupied' : 'Available'}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mb-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7h-3.771a1 1 0 01-.994.89h-1.47a1 1 0 01-.994-.89zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-[10px] font-bold">B-{i + 1}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 text-right">
              <button onClick={() => setShowBedModal(false)} className="bg-gray-700 hover:bg-gray-600 text-light py-2 px-6 rounded-lg font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalDashboard;
